import { OfferLetterFormsService } from 'api-clients/monolith/services/OfferLetterFormsService';
import { OfferLettersService } from 'api-clients/monolith/services/OfferLettersService';
import { Signer } from 'containers/OfferLetterTemplates/types';

import { readTemplateOffer } from './documents';
import { isEmittable } from './SignaturePlacement/geometry';
import {
  PLACEMENT_FIELD_TYPES,
  PlacementFieldType,
  SignatureFieldPlacement,
} from './SignaturePlacement/types';
import {
  ApprovalState,
  ApproverGroup,
  ConditionalBlock,
  ConditionOperator,
  OfferLetterFormData,
  OfferPreviewData,
  PreviewResult,
  VisibilityCondition,
} from './types';

type RawForm = Awaited<
  ReturnType<typeof OfferLetterFormsService.getInternalApiApplicantsOfferLetterForm>
>;
type RawPreview = Awaited<
  ReturnType<typeof OfferLetterFormsService.postInternalApiApplicantsOfferLetterFormPreview>
>;

// The generated client types every field optional; normalize to the strict
// view the UI works with (empty/false/null defaults).
const normalizeApproval = (
  approval: RawForm['approval'] | RawPreview['approval'],
): ApprovalState => ({
  required: approval?.required ?? false,
  triggered_rules: (approval?.triggered_rules ?? []).map(rule => ({
    label: rule.label ?? '',
    description: rule.description ?? '',
    field_key: rule.field_key ?? null,
  })),
});

// AXHE-4187. `visible_when` on a field and `included_when` on a block are the
// same rule shape, so they normalize through one function — a second copy is a
// place for the two to drift on what a missing operator falls back to.
const normalizeCondition = (condition: {
  data_key?: string | null;
  operator?: ConditionOperator | null;
  value?: string | null;
}): VisibilityCondition => ({
  data_key: condition.data_key ?? '',
  operator: condition.operator ?? 'equals',
  value: condition.value ?? null,
});

// Shared by the form definition and the preview response, which return the
// same block shape on purpose (the controller builds both from one call).
const normalizeBlock = (block: {
  key?: string | null;
  label?: string | null;
  included?: boolean | null;
  included_when?: { data_key?: string | null; operator?: ConditionOperator | null; value?: string | null }[] | null;
}): ConditionalBlock => ({
  key: block.key ?? '',
  label: block.label ?? null,
  included: block.included ?? false,
  // Defaults to [] — which for a BLOCK means EXCLUDED, not included, matching
  // the server's default for a block nobody configured. A backend that predates
  // this field would therefore look like "every section excluded", but that
  // cannot hide anything: such a payload also carries no `block_occurrences`,
  // and a field with no occurrences is never withdrawn. The two defaults are
  // safe together, which is the point of ANDing them.
  included_when: (block.included_when ?? []).map(normalizeCondition),
});

export const normalizeForm = (raw: RawForm): OfferLetterFormData => ({
  offer_letter_template: raw.offer_letter_template
    ? {
        id: raw.offer_letter_template.id ?? '',
        title: raw.offer_letter_template.title ?? '',
      }
    : null,
  available_templates: (raw.available_templates ?? [])
    .filter((template): template is NonNullable<typeof template> => template != null)
    .map(template => ({
      id: template.id ?? '',
      title: template.title ?? '',
      // The per-template offer state driving the document list (AXHE-4181).
      // `readTemplateOffer` is the single reader of that payload — see documents.ts.
      offer: readTemplateOffer(template),
    })),
  fields: (raw.fields ?? []).map(field => ({
    key: field.key ?? '',
    label: field.label ?? null,
    kind: field.kind ?? 'manual_entry',
    data_type: field.data_type ?? 'text',
    currency_format: field.currency_format ?? false,
    spell_out: field.spell_out ?? false,
    // `formulas` is `jsonb not null` with no array-shape validation on the
    // model, so a stray migration or a hand-written console edit can leave it a
    // non-array value OR an array with a non-string member. Guard both: without
    // the member filter, a value like `[{}]` survives here and reaches
    // `formula.ts`'s `parseFormula`, where `normalizeFormulaOperators(...).trim()`
    // calls `.split`/`.trim` on the object and crashes Send Offer for every
    // recruiter opening that template — the same guard VariableRow.tsx applies
    // on the authoring side.
    formulas: Array.isArray(field.formulas)
      ? field.formulas.filter((formula): formula is string => typeof formula === 'string')
      : [],
    editable: field.editable ?? true,
    prefilled: field.prefilled ?? false,
    value: field.value ?? '',
    data_key: field.data_key ?? null,
    // Same null-vs-[] rule as a condition field's below — see the comment there
    // (AXHE-4180).
    allowed_values: field.allowed_values ?? null,
    // AXHE-4187. A field the server didn't report on is one with no conditions
    // — always asked — so both default to "shown, unconditionally". That is
    // also the shape every payload had before this ticket, so an older backend
    // renders exactly as it did.
    visible: field.visible ?? true,
    visible_when: (field.visible_when ?? []).map(normalizeCondition),
    // AXHE-4187, the section half. [] means "no occurrences", which the section
    // rule reads as "cannot withdraw this field" — so a payload from a backend
    // that predates this key behaves exactly as it did.
    block_occurrences: (field.block_occurrences ?? []).map(chain => [...(chain ?? [])]),
  })),
  condition_fields: (raw.condition_fields ?? []).map(field => ({
    key: field.key ?? '',
    label: field.label ?? null,
    kind: 'condition' as const,
    // null and [] mean different things here and must NOT be collapsed: null is
    // "these conditions can't be enumerated, render a free input", while an
    // empty array would render a select with nothing to pick. The server never
    // sends [], but the generated client types it optional, so a missing value
    // has to fall to null rather than to [].
    allowed_values: field.allowed_values ?? null,
    editable: field.editable ?? true,
    prefilled: field.prefilled ?? false,
    value: field.value ?? '',
  })),
  conditional_blocks: (raw.conditional_blocks ?? []).map(normalizeBlock),
  approval: normalizeApproval(raw.approval),
});

export const normalizePreview = (raw: RawPreview): PreviewResult => ({
  approval: normalizeApproval(raw.approval),
  missing_fields: (raw.missing_fields ?? []).map(missing => ({
    key: missing.key ?? '',
    reason: missing.reason ?? 'missing_value',
  })),
  can_generate: raw.can_generate ?? false,
  conditional_blocks: (raw.conditional_blocks ?? []).map(normalizeBlock),
});

type RawOffer = Awaited<
  ReturnType<typeof OfferLettersService.postInternalApiApplicantsOfferLetters>
>;

const PLACEMENT_TYPES = new Set<string>(PLACEMENT_FIELD_TYPES);

/**
 * Coerce the stored `signature_field_placements` jsonb into the editor's view
 * (AXHE-4085).
 *
 * The column is schemaless, so a row written by an older client — or by a
 * console fix during an incident — can be any shape at all. A malformed row is
 * dropped rather than repaired: a field the recruiter can neither see nor
 * correct is worse than one that is missing from the editor, and the same
 * validation on the backend would reject it at send time anyway.
 */
export const normalizePlacements = (raw: unknown): SignatureFieldPlacement[] => {
  if (!Array.isArray(raw)) return [];

  return raw
    .filter(
      (row): row is Record<string, unknown> =>
        typeof row === 'object' && row !== null,
    )
    .filter(row => PLACEMENT_TYPES.has(String(row.type)))
    .map(row => ({
      type: row.type as PlacementFieldType,
      signer: Number(row.signer) || 0,
      page: Number(row.page) || 0,
      x: Number(row.x) || 0,
      y: Number(row.y) || 0,
      width: Number(row.width) || 0,
      height: Number(row.height) || 0,
      // Absent means required, matching FormFieldsBuilder's `fetch(:required, true)`.
      required: row.required !== false,
    }))
    .filter(isEmittable);
};

export const normalizeOfferPreview = (raw: RawOffer): OfferPreviewData => {
  const groups: ApproverGroup[] = (raw.approval?.groups ?? []).map(group => ({
    rule: {
      label: group.rule?.label ?? '',
      description: group.rule?.description ?? '',
      field_key: group.rule?.field_key ?? null,
    },
    approvers: (group.approvers ?? []).map(approver => ({
      name: approver.name ?? '',
      email: approver.email ?? '',
    })),
  }));

  // The generated client is rebuilt from the monolith's OpenAPI spec, and
  // AXHE-4085's two keys are not in it yet. Reading them off a widened view
  // keeps the hand-editing out of api-clients/, which is generated.
  // AXHE-4266's `can_revert_to_template` joins them for the same reason: the
  // backend half (#3608) regenerates the client, but this ships first.
  const extended = raw as RawOffer & {
    signature_field_placements?: unknown;
    signers?: Partial<Signer>[];
    can_revert_to_template?: boolean;
  };

  return {
    id: raw.id ?? '',
    status: raw.status ?? 'draft',
    // The offer preview schema types contract_type as a plain string; it's one
    // of the known contract types at runtime (server-sourced).
    contract_type: (raw.contract_type ?? null) as OfferPreviewData['contract_type'],
    field_values: raw.field_values ?? {},
    rendered_pdf_url: raw.rendered_pdf_url ?? null,
    approval: { required: raw.approval?.required ?? false, groups },
    cta: raw.cta ?? 'send_to_candidate',
    source: (raw.source ?? 'generated') as OfferPreviewData['source'],
    upload_override_enabled: raw.upload_override_enabled ?? false,
    // Defaults to false, which hides the revert control. That is the safe
    // default in both directions: against a backend that predates AXHE-4266 the
    // endpoint doesn't exist, and against one that does, a payload we couldn't
    // read the flag from is not evidence the revert would be accepted. A missing
    // control is recoverable — the recruiter can still re-upload — where one
    // that 422s on press is not (AXHE-4285).
    can_revert_to_template: extended.can_revert_to_template ?? false,
    signature_field_placements: normalizePlacements(
      extended.signature_field_placements,
    ),
    signers: extended.signers ?? [],
  };
};
